<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div class="iq-card mb-3" style="min-width: 300px;">
    <div class="iq-card-header d-flex justify-content-between">
      <div class="iq-header-title py-3">
        <h4 class="card-title">{{ config.title }} Detail</h4>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="text-right">
            <button-options :access="canRead(config.accessKey)" :hideDetail="true" :isBackEnabled="true"
              :isRefreshEnabled="true" :refreshed="isLoading" @onRefreshed="getAssigned('Refreshed')" />
          </div>
        </div>
      </div>
    </div>
    <div class="iq-card-body">
      <div class="d-flex flex-wrap align-items-end" style="gap: 1rem;">
        <div role="group" class="form-group mb-0" style="flex: 1 1 260px;">
          <label class="d-block form-label text-left">
            {{ config.title }}:
          </label>
          <div class="bv-no-focus-ring">
            <v-select
              :label="config.entityNameKey"
              :placeholder="'Select ' + config.title"
              v-model="selectedEntityId"
              :disabled="isSubmit"
              :options="availableOptions"
              :reduce="option => option[config.entityIdKey]"
              @search="onSearch">
              <template slot="no-options">
                {{ availableOptions.length > 0 ? `Type to search ${config.title}` : `No ${config.title} available` }}
              </template>
            </v-select>
          </div>
        </div>
        <div v-if="config.extraField" role="group" class="form-group mb-0">
          <label class="d-block form-label text-left">
            {{ config.extraField.label }}:
          </label>
          <b-form-checkbox-group v-if="config.extraField.type === 'checkboxes'"
            v-model="extraValue" :disabled="isSubmit" :options="config.extraField.options"
            buttons button-variant="outline-primary" size="sm"></b-form-checkbox-group>
          <select v-else class="form-control" v-model="extraValue" :disabled="isSubmit">
            <option v-for="opt in config.extraField.options" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </div>
        <div class="form-group mb-0">
          <b-button size="sm" type="submit" variant="primary" :disabled="isSubmit || !selectedEntityId"
            @click="onSubmit">
            <div class="mx-2">
              <i class="fa" :class="isSubmit ? 'ri-loader-line' : 'ri-save-line'"></i>
              Submit
            </div>
          </b-button>
        </div>
      </div>
    </div>
    <div class="iq-card-body">
      <div class="row">
        <div class="col-md-12">
          <div class="table-responsive">
            <b-table fixed show-empty responsive :busy="isLoading" :fields="tableFields" :items="tableItems"
              head-variant="dark" style="border-radius: 10px;">
              <template v-slot:table-busy="">
                <div class="text-center text-danger my-2">
                  <b-spinner variant="success" size="sm" class="align-middle"></b-spinner>
                </div>
              </template>
              <template v-slot:cell(extraValue)="data">
                <b-form-checkbox-group
                  v-if="config.extraField && config.extraField.type === 'checkboxes' && config.updateAssigned"
                  v-model="editableExtraValues[rowKey(data.item.raw)]"
                  :disabled="isSubmit || isLoading" :options="config.extraField.options"
                  buttons button-variant="outline-primary" size="sm"></b-form-checkbox-group>
                <span v-else-if="Array.isArray(data.value)">{{ data.value.join(', ') }}</span>
                <template v-else>
                  <b-badge v-if="Number(data.value) === 1" variant="success">YES</b-badge>
                  <b-badge v-else variant="danger">NO</b-badge>
                </template>
              </template>
              <template v-slot:cell(action)="data">
                <div class="text-left text-nowrap">
                  <button v-if="config.updateAssigned" class="btn btn-primary m-1 btn-sm"
                    :disabled="isSubmit || isLoading" @click="saveRowExtra(data.item.raw)">
                    <i class="ri-save-line"></i>
                    Save
                  </button>
                  <button class="btn btn-light m-1 btn-sm" :disabled="isSubmit || isLoading"
                    @click="openDelete(data.item.raw)">
                    <i class="ri-delete-bin-7-line"></i>
                    Delete
                  </button>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <b-pagination v-model="pagination.current" :per-page="pagination.pageSize"
            :total-rows="pagination.totalSize" @change="onPageChanged"></b-pagination>
        </div>
      </div>
    </div>
    <b-modal
      size="lg"
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      v-model="displayDeletion">
      <delete-item
        :isSubmit="isSubmit"
        @onCancelDeletion="displayDeletion = false"
        @onConfirmDeletion="onConfirmDeletion"/>
    </b-modal>
  </div>
</template>

<script>
import ButtonOptions from '@/components/directives/button-options';
import DeleteItem from '@/components/directives/delete-item';

export default {
  name: 'RelationManager',
  components: { ButtonOptions, DeleteItem },
  props: {
    config: { type: Object, required: true },
    parentId: { type: [String, Number], required: true },
  },
  data() {
    return {
      selectedEntityId: null,
      extraValue: this.config.extraField ? this.config.extraField.default : undefined,
      isLoading: false,
      isSubmit: false,
      displayDeletion: false,
      selectedForDelete: {},
      relatedPool: [],
      searchPool: null,
      assignedItems: [],
      editableExtraValues: {},
      pagination: {
        current: 1,
        pageSize: 25,
        totalSize: 0,
      },
    };
  },
  computed: {
    optionsSource() {
      return this.searchPool !== null ? this.searchPool : this.relatedPool;
    },
    availableOptions() {
      const assignedIds = new Set(
        this.assignedItems.map((row) => this.config.getAssignedEntityId(row)),
      );
      return this.optionsSource.filter((item) => !assignedIds.has(item[this.config.entityIdKey]));
    },
    tableItems() {
      return this.assignedItems.map((row) => ({
        raw: row,
        entityName: this.entityName(row),
        extraValue: this.config.extraField ? row[this.config.extraField.key] : undefined,
      }));
    },
    tableFields() {
      const fields = [{ key: 'entityName', label: `List of ${this.config.title}`, class: 'text-left' }];
      if (this.config.extraField) {
        fields.push({
          key: 'extraValue',
          label: this.config.extraField.columnLabel,
          class: 'text-left',
          thStyle: { width: '1%', whiteSpace: 'nowrap' },
        });
      }
      fields.push({
        key: 'action', label: '', class: 'text-center', thStyle: { width: '1%', whiteSpace: 'nowrap' },
      });
      return fields;
    },
  },
  methods: {
    entityName(row) {
      if (this.config.getAssignedEntityName) return this.config.getAssignedEntityName(row);
      const id = this.config.getAssignedEntityId(row);
      const found = this.relatedPool.find((item) => item[this.config.entityIdKey] === id);
      return found ? found[this.config.entityNameKey] : '';
    },
    rowKey(row) {
      return this.config.getAssignedEntityId(row);
    },
    async getAssigned(notification) {
      this.isLoading = true;
      try {
        const { items, totalSize } = await this.config.fetchAssigned(this.parentId, this.pagination);
        this.assignedItems = items;
        if (this.config.extraField) {
          this.editableExtraValues = Object.fromEntries(items.map((row) => {
            const value = row[this.config.extraField.key];
            return [this.rowKey(row), Array.isArray(value) ? [...value] : value];
          }));
        }
        this.pagination.totalSize = totalSize;
        if (notification) this.$toast.success(notification);
      } catch (e) {
        if (e.response && e.response.data) {
          if (e.response.data.status.message === 'Authentication Error') {
            this.doLogOut();
          } else {
            this.$toast.error(e.response.data.status.message);
          }
        } else {
          this.$toast.error('Network error');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getRelatedAll() {
      try {
        this.relatedPool = (await this.config.fetchRelatedAll()) || [];
      } catch (e) {
        this.relatedPool = [];
        this.$toast.error(e.message);
      }
    },
    async onSearch(term) {
      if (!term) {
        this.searchPool = null;
        return;
      }
      try {
        this.searchPool = await this.config.searchRelated(term);
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
    async onSubmit() {
      this.isSubmit = true;
      try {
        const extra = this.config.extraField
          ? { [this.config.extraField.key]: this.extraValue }
          : {};
        const res = await this.config.createAssigned(this.parentId, this.selectedEntityId, extra);
        if (res.data.status.error === 0) {
          this.selectedEntityId = null;
          this.searchPool = null;
          this.extraValue = this.config.extraField ? this.config.extraField.default : undefined;
          await this.getRelatedAll();
          await this.getAssigned(`${this.config.title} added`);
        } else {
          this.$toast.error(res.data.status.message);
        }
      } catch (e) {
        this.$toast.error(e.response.data.status.message);
      } finally {
        this.isSubmit = false;
      }
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getAssigned();
    },
    async saveRowExtra(row) {
      this.isSubmit = true;
      try {
        const res = await this.config.updateAssigned(row, this.editableExtraValues[this.rowKey(row)]);
        if (res.data.status.error === 0) {
          await this.getAssigned('Updated');
        } else {
          this.$toast.error(res.data.status.message);
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    openDelete(row) {
      this.displayDeletion = true;
      this.selectedForDelete = row;
    },
    onConfirmDeletion() {
      this.deleteData('Delete complete');
    },
    async deleteData(notification) {
      this.isSubmit = true;
      try {
        const res = await this.config.deleteAssigned(this.selectedForDelete);
        if (res.data.status.error === 0) {
          this.displayDeletion = false;
          this.pagination.current = 1;
          await this.getAssigned(notification);
          await this.getRelatedAll();
        } else {
          this.$toast.error(res.data.status.message);
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    doLogOut() {
      this.$toast.error('Please re-login');
      this.$store.dispatch('auth/logout');
      this.$router.replace({ name: 'Login' });
    },
  },
  async mounted() {
    await this.getRelatedAll();
    await this.getAssigned();
  },
};
</script>

<style scoped></style>
